import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { useToast } from "@/components/feedback";
import { useAccessToken } from "@/hooks/shared/useAccessToken";
import { useTenantData } from "@/hooks/shared/useTenantData";
import { createAuthenticatedApi } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/utils/api/error-message";
import type { CreateTuitionRatePlanRequest } from "@/openapi/generated";
import { TuitionTiersApi } from "@/openapi/generated";
import { invalidateTuitionRatePlanQueries } from "./invalidate-tuition-rate-queries";

export function useTuitionRatePlanCreate(toast: ReturnType<typeof useToast>) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { tenantId } = useTenantData();

  return useMutation({
    mutationFn: async (
      createTuitionRatePlanRequest: CreateTuitionRatePlanRequest,
    ) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const api = createAuthenticatedApi(TuitionTiersApi, accessToken);
      return api.apiTuitionTiersRatePlansPost({
        createTuitionRatePlanRequest,
        xTenantID: tenantId,
      });
    },
    onSuccess: async () => {
      await invalidateTuitionRatePlanQueries(queryClient);
      toast({
        title: "Rate plan created",
        description:
          "Add pricing tiers, then attach this plan to a season or class.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error) => {
      const message = await getApiErrorMessage(
        error,
        "Failed to create rate plan.",
      );
      toast({
        title: "Error",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });
}
